import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { facilitiesService } from '../../services/facilities';
import { helpService } from '../../services/help';
import GuidedTour, { TourStep } from './GuidedTour';
import { tourSteps } from './tourSteps';
import ReportProblem from './ReportProblem';
import './Help.css';

/** Shared accessible Help menu and one-time tour controller. */
export default function HelpMenu() {
  const { currentUser, setCurrentUser, bootstrap } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const portal = currentUser?.role === 'client' || currentUser?.role === 'facility_manager';
  const tourKey = portal ? 'tour_portal_v1' : 'tour_consultant_v1';
  const [open, setOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [latest, setLatest] = useState('');
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const attempted = useRef('');
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const unseen = !!latest && latest !== currentUser?.ui_state?.last_seen_release;
  useEffect(() => { if (bootstrap) setLatest(bootstrap.changelog_latest.version); }, [bootstrap]);
  const start = useCallback(async () => {
    setOpen(false); setStarting(true); setError('');
    try {
      const facilities = portal ? [] : await facilitiesService.getFacilities();
      setSteps(tourSteps(portal, facilities[0]));
    } catch { setError('Could not load the tour. Please try again from Help.'); }
    finally { setStarting(false); }
  }, [portal]);
  useEffect(() => {
    const launch = () => { void start(); };
    window.addEventListener('h2okay:start-help-tour', launch);
    return () => window.removeEventListener('h2okay:start-help-tour', launch);
  }, [start]);
  useEffect(() => {
    if (currentUser?.profile_completed_at && !currentUser.ui_state?.[tourKey] && currentUser.role !== 'h2okay_admin' && attempted.current !== currentUser.id) {
      attempted.current = currentUser.id; void start();
    }
  }, [currentUser, tourKey, start]);
  useEffect(() => {
    if (open) menu.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [open]);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => { if (!menu.current?.contains(event.target as Node) && !button.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);
  const complete = useCallback(async (result: 'dismissed' | 'finished') => {
    const user = await helpService.saveState({ [tourKey]: result === 'dismissed' ? 'dismissed' : new Date().toISOString() });
    setCurrentUser(user); setSteps(null); button.current?.focus();
  }, [tourKey, setCurrentUser]);
  return <div className="help-menu-wrap">
    <button ref={button} data-tour="help" className="notification-btn help-trigger" aria-label="Help" aria-haspopup="menu" aria-expanded={open} aria-controls="help-menu" disabled={starting} onClick={() => setOpen(value => !value)} onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true); } }}>?{unseen && <span className="help-dot" aria-label="New release notes" />}</button>
    {open && <div id="help-menu" ref={menu} role="menu" aria-label="Help" className="help-menu" onKeyDown={event => {
      const items = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
      const index = items.indexOf(document.activeElement as HTMLButtonElement);
      if (event.key === 'Escape') { setOpen(false); button.current?.focus(); }
      else if (event.key === 'Tab') setOpen(false);
      else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) { event.preventDefault(); items[event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus(); }
    }}>
      <button role="menuitem" tabIndex={-1} onClick={() => void start()}>Take the tour</button>
      <button role="menuitem" tabIndex={-1} onClick={() => { setOpen(false); navigate(portal ? '/portal/whats-new' : '/whats-new'); }}>What's new{unseen && <span className="help-dot" />}</button>
      <button role="menuitem" tabIndex={-1} onClick={() => { setOpen(false); setReporting(true); }}>Report a problem</button>
    </div>}
    {error && <p role="alert">{error}</p>}
    {steps && <GuidedTour steps={steps} onComplete={complete} />}
    {reporting && <ReportProblem onClose={() => { setReporting(false); button.current?.focus(); }} />}
  </div>;
}
